import React, { useState } from "react";
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';

// ------- MUTATIONS
import CREATE_ROYALTY_MUTATION from "../../graphql/mutations/Royaltys/CreateRoyalty.js";
import CREATE_ROYALTY_LINE_ITEM_MUTATION from "../../graphql/mutations/Royaltys/CreateRoyaltyLineItem.js";


// ------- COMPONENTS
import { baseRoyaltyObject } from './BaseRoyaltyObject.js';
import RoyaltyLineItem from "./RoyaltyLineItem.js";
import {NotSeeingItems} from "../../components/NotSeeingItems.js";
import { SelectCreator, ForeignKeyDropDownInput } from "../../inputs/index.js";

import {
  Pane,
  Paragraph,
  TextInput,
  Button,
  Card,
  Table,
  AddIcon,
  ArrowDownIcon,
  toaster,
  SelectMenu,
  Spinner,
  HelpIcon,
  Popover,
} from 'evergreen-ui';

function NewRoyaltyForm({handleToggleNewMode, handleRefetchRoyaltys, data, publisher}) {
  const publisherGlobalData = useSelector((state) => state.publisherGlobalData.value);
  const enableSalesByRegion = publisherGlobalData.enable_sales_by_region;

  const initialRegion = enableSalesByRegion ? { id: null, name: "Rest of World" } : null;
  const [staged, setStaged] = useState({ ...baseRoyaltyObject(), region: initialRegion });
  const [duplicateRoyaltyError, setDuplicateRoyaltyError] = useState("");

  // Let's print the staged state to see what we're working with
  console.log("Current staged state:", staged);

  const [productSet, setProductSet] = useState([])
  const [canSave, setCanSave] = useState(false);
  const [items, setItems] = useState([{
    tmpid: 1,
    amount: "",
    thresholdStart: "",
    thresholdEnd: "",
    royaltyTerm: "",
    category: { id: 1, value: "units sold" },
    tierDiscount: 'None',
    tmpids: []
  }
  ]);
  const [idCount, setIdCount] = useState(2);


  // Use available regions or fallback to defaults
  const availableRegionsFromGlobalData =
    Array.isArray(publisherGlobalData?.regions) && publisherGlobalData?.regions.length > 0
      ? publisherGlobalData.regions.map(region => ({
          id: parseInt(region.id, 10), // Ensure ID is integer
          name: region.name
        }))
      : [];

  const availableRegions = enableSalesByRegion
    ? [{ id: null, name: "Rest of World" }, ...availableRegionsFromGlobalData]
    : availableRegionsFromGlobalData;

  console.log("NewRoyaltyForm - Available regions:", availableRegions);
  console.log("NewRoyaltyForm - Enable sales by region:", enableSalesByRegion);

  // Provide fallback royalty terms if they're missing in publisherGlobalData
  const fallbackRoyaltyTerms = [
    { id: 1, value: "of list price" },
    { id: 2, value: "of net receipts" },
    { id: 3, value: "of net profits" },
    { id: 4, value: "of true profits" }
  ];

  // Use available royalty terms or fallback to defaults
  const royaltyTerms =
    Array.isArray(publisherGlobalData?.royaltyterms) && publisherGlobalData?.royaltyterms.length > 0
      ? publisherGlobalData.royaltyterms
      : fallbackRoyaltyTerms;

  console.log("NewRoyaltyForm - Using royalty terms:", royaltyTerms);

  const [createRoyalty, { royaltydata }] = useMutation(CREATE_ROYALTY_MUTATION);
  const [createRoyaltyLineItem, { itemdata }] = useMutation(CREATE_ROYALTY_LINE_ITEM_MUTATION);


  const handleClearStaged = () => {
    setStaged(baseRoyaltyObject());
    setDuplicateRoyaltyError("");
  }

  const handleCreateRoyalty = async () => {
    let newStagedForMutation = JSON.parse(JSON.stringify(staged));
    delete newStagedForMutation.royaltylineitemSet;
    delete newStagedForMutation.royaltypaymentSet;

    // If region is "Rest of World", remove it from the royalty JSON blob
    if (newStagedForMutation.region && newStagedForMutation.region.id === null) {
      delete newStagedForMutation.region;
    }

    const mutationVariables = {
      publisherId: parseInt(publisher),
      royalty: JSON.stringify(newStagedForMutation)
    };

    // Send regionId separately ONLY if it's a specific region (not "Rest of World")
    if (enableSalesByRegion && staged.region && staged.region.id !== null) {
      mutationVariables.regionId = parseInt(staged.region.id);
    }

    try {
      const results = await createRoyalty(
        {
          variables: mutationVariables
        });
      const allCreated = await Promise.all(items.map(async (item) => {
        let royaltyItem = {
          amount: item.amount,
          thresholdStart: item.thresholdStart,
          thresholdEnd: item.thresholdEnd,
          royaltyTerm: item.royaltyTerm,
          category: item.category,
          tierDiscount: item.tierDiscount,
        }
        const created = await createRoyaltyLineItem(
          {
            variables: {
              royaltyId: parseInt(results.data.createRoyalty.message),
              royaltyItem: JSON.stringify(royaltyItem)
            }
          }
        )
        return created

      }))
      toaster.closeAll()
      toaster.success(`Created a new royalty for ${staged.creator.displayName} for ${staged.product?.productWork?.title} with ${allCreated.length} tiers`, { duration: 10 })

      handleClearStaged();
      setDuplicateRoyaltyError("");
      handleToggleNewMode();
      handleRefetchRoyaltys();
    } catch (error) {
      console.error("Error creating royalty:", error);
      toaster.closeAll()
      if (error.graphQLErrors && error.graphQLErrors.length > 0 && error.graphQLErrors[0].message.includes("A royalty already exists")) {
        setDuplicateRoyaltyError(error.graphQLErrors[0].message);
      } else {
        toaster.danger("An error occurred while creating the royalty. Please try again later.");
        setDuplicateRoyaltyError("");
      }
    }
  }
  const handleUpdateStaged = obj => {
    console.log("handleUpdateStaged called with:", obj);
    setDuplicateRoyaltyError("");

    // For region specifically
    if (obj.key === "region") {
      console.log("Updating region:", obj.val);
      console.log("Region properties:", {
        hasName: obj.val && 'name' in obj.val,
        hasValue: obj.val && 'value' in obj.val,
        name: obj.val?.name,
        value: obj.val?.value,
        id: obj.val?.id
      });
    }

    // Use this to debug what's happening after the update
    const oldState = { ...staged };
    const newState = { ...oldState, [obj.key]: obj.val };
    console.log(`State change for ${obj.key}:`, {
      oldValue: oldState[obj.key],
      newValue: newState[obj.key]
    });

    setStaged(oldState => ({ ...oldState, [obj.key]: obj.val }));
  }

  const handleAttachProduct = selectedItem => {
    // Find the product object from productSet based on the ID in selectedItem.value
    const product = productSet.find(p => p.id === selectedItem.value);
    if (!product) {
      console.error("Selected product not found in productSet:", selectedItem);
      toaster.warning("Could not find the selected product. Please try again.");
      return; // Exit if product not found
    }

    handleUpdateStaged({
      "key": "product",
      "val": product // Use the found product object
    })
    checkIfValid(items, true)
  }

  const handleAttachCreator = creatorString => {
    let creator = JSON.parse(creatorString)
    // SIMPLIFIED: Get all non-frozen products associated with the creator
    let productSet = creator.productcreatorSet
        .filter(item => !item.product.frozen)
        .map(item => item.product);

    setProductSet(productSet)

    handleUpdateStaged({
      "key": "creator",
      "val": creator
    })

    handleUpdateStaged({
      "key": "product",
      "val": ""
    })
    // Explicitly reset region when creator changes
    const newRegionValue = enableSalesByRegion ? { id: null, name: "Rest of World" } : null;
    handleUpdateStaged({
      "key": "region",
      "val": newRegionValue
    });
    checkIfValid(items, staged.product?.id ? true : false); // Pass current product status
  }

  const handleAddItem = (category, discount) => {
    const lastTier = items.filter(item => item.last === true).filter(item => item.tierDiscount === discount)[0]
    let tmpid = idCount;
    let newarr = [{
      tmpid: tmpid + 1,
      amount: "",
      thresholdStart: lastTier ? lastTier.thresholdStart + 100 : 0,
      thresholdEnd: "",
      royaltyTerm: "",
      category: category,
      tierDiscount: discount,
      tmpids: []
    }].concat(items);
    tmpid += 1;
    setIdCount(tmpid);
    setEndThresholds(newarr, discount)
    setItems(newarr);
    checkIfValid(newarr, staged.product?.id ? true : false);
  }

  const handleUpdateItem = item => {
    let newarr = items.filter(ri => ri.tmpid !== item.tmpid);
    newarr.push(item);
    let disc = item.tierDiscount;
    setEndThresholds(newarr, disc)
    setItems(newarr);
    checkIfValid(newarr, staged.product?.id ? true : false);
  }

  const handleRemoveItem = lineitem => {
    let newarr = [].concat(items);
    newarr = newarr.filter(item => item.tmpid !== lineitem.tmpid);
    setEndThresholds(newarr, lineitem.tierDiscount)
    setItems(newarr);
    checkIfValid(newarr, staged.product?.id ? true : false);
  }

  const setEndThresholds = (itemarr, d) => {
    let discarr = itemarr.filter(item => item.tierDiscount === d)
    discarr.sort((a, b) => (a.tmpid > b.tmpid) ? 1 : ((b.tmpid > a.tmpid) ? -1 : 0))
    discarr.forEach((item, index) => {
      if (index === 0) {
        item.thresholdStart = 0;
      }
      else if (index > 0) {
        discarr[index - 1].thresholdEnd = String(item.thresholdStart - 1)
      }
      if (index === discarr.length - 1) {
        item.thresholdEnd = "\u{221E}"
      }
    })
    discarr.forEach((item, index) => {
      if (index > 0) {
        item.invalid = item.thresholdStart <= discarr[index - 1].thresholdStart

      }

    })
  }

  const sortItems = (arr, category) => {
    arr = arr.filter(item => item.category.id === category)
    let discountList = sortItemsByDiscount(arr)

    let newarr = [];
    discountList.forEach((d, index) => {
      let discarr = arr.filter(item => item.tierDiscount === d)
      discarr.sort((a, b) => (a.tmpid > b.tmpid) ? 1 : ((b.tmpid > a.tmpid) ? -1 : 0))
      discarr.forEach((item) => { item.first = false; item.last = false; item.tierCount = discarr.length; })

      discarr[0].first = true;
      discarr[discarr.length - 1].last = true;
      newarr = newarr.concat(discarr)
    })

    return newarr
  }

  const sortItemsByDiscount = arr => {
    let discountList = arr.map(item => item.tierDiscount).filter((v, i, a) => a.indexOf(v) === i);
    let discountTmpids = [];

    discountList.forEach((d) => {
      let temparr = arr.filter(item => item.tierDiscount === d)
      let tmpids = temparr.map(item => item.tmpid)
      temparr.forEach(item => { item.tmpids = tmpids })
      discountTmpids.push({ discount: d, tmpid: Math.min(...tmpids), tmpids: tmpids })
    })

    discountTmpids = discountTmpids.sort((a, b) => (a.tmpid > b.tmpid) ? 1 : ((b.tmpid > a.tmpid) ? -1 : 0))
    discountList = discountTmpids.map(item => item.discount)

    return discountList

  }


  const checkIfValid = (currentItems, prodAttached) => {
    const invalidTier = currentItems.some(item => {
      return (!item.royaltyTerm || item.invalid || !item.amount)
    }) || currentItems.length < 1;

    let invalid = invalidTier || !prodAttached;
    let frontEndDuplicateMessage = "";

    // Check for duplicate royalty (Creator + Product + Region)
    if (Array.isArray(data) && staged.creator?.id && staged.product?.id) {
        const currentRegionId = staged.region ? staged.region.id : null; // Note: region.id can be null for "Rest of World"
        const duplicateExists = data.some(existingRoyalty => {
            const existingRegionId = existingRoyalty.region ? existingRoyalty.region.id : null;
            return (
                parseInt(existingRoyalty.creator.id) === parseInt(staged.creator.id) &&
                parseInt(existingRoyalty.product.id) === parseInt(staged.product.id) &&
                existingRegionId === currentRegionId
            );
        });

        if (duplicateExists) {
            frontEndDuplicateMessage = `A royalty already exists for ${staged.creator.displayName} - ${staged.product.productWork?.title || staged.product.title} ${staged.region && staged.region.id !== null ? `in region ${staged.region.name}` : '(Rest of World)'}.`;
        }
    }

    setDuplicateRoyaltyError(frontEndDuplicateMessage);

    if (invalid || frontEndDuplicateMessage) {
      setCanSave(false);
    } else {
      setCanSave(true);
    }
  }

  const handleChangeDiscount = e => {
    let tmpids = items.filter(a => a.tmpid.toString() === e.target.id)[0].tmpids
    tmpids.forEach((id) => {
      items.filter(a => a.tmpid === id)[0].tierDiscount = e.target.value
    })
  }

  return (
    <Pane backgroundColor="var(--background)" width="100%" height="100%">
      <Pane borderBottom="solid" borderBottomColor="#e6e4e0" borderBottomWidth="1px" paddingX={16} paddingBottom={8} paddingTop={16} width="100%" display="flex" justifyContent="space-between" alignItems="flex-end">
        <Paragraph fontSize={18}> New Royalty</Paragraph>
        <Pane display="flex" flexDirection="row" justifyContent="flex-start">
          <Button disabled={!canSave || royaltydata?.loading || itemdata?.loading} marginRight={16} className="confirm-button" onClick={e => {
            handleCreateRoyalty();
          }}>
            CREATE ROYALTY
            {(royaltydata?.loading || itemdata?.loading) && <Spinner marginLeft={8} size={12}/>}


          </Button>
          <Button onClick={e => {
            handleClearStaged();
            handleToggleNewMode();
          }}>
            Cancel
          </Button>
        </Pane>

      </Pane>
      <Pane padding={16}>
        <Card backgroundColor="white" padding={16} elevation={1}>
          <Pane display="flex" flexDirection="row" alignItems="flex-end" justifyContent="flex-start">
            <Pane marginY={16}>
              <Pane display="flex" alignItems="center">
                <Paragraph color="#667b7f" size={300}>Creator</Paragraph>
                <Popover
                  content={
                    <Pane padding={12}>
                      <Paragraph size={300}>
                        Only creators that have been associated with a product show here.
                      </Paragraph>
                    </Pane>
                  }
                >
                  <HelpIcon size={12} marginLeft={8} color="muted" />
                </Popover>
              </Pane>

              <SelectCreator
                handleNewCreator={handleAttachCreator}
                parent="royalties"
                royalties={data}
                enableSalesByRegion={enableSalesByRegion}
              />


            </Pane>

            <Pane marginY={16} marginLeft={40}>
              <Paragraph color="#667b7f" size={300}>Product </Paragraph>
              <Pane >
                <SelectMenu
                  title="Select Product"
                  options={productSet.map(product => {
                    const title = product.productWork ? product.productWork.title : (product.title || 'No Title Found');
                    const format = product.format ? product.format.description : 'No Format';
                    const isbn = product.isbn || 'No ISBN';
                    return { label: `${title} (${format} - ${isbn})`, value: product.id };
                  })}
                  selected={staged.product ? staged.product.id : null}
                  onSelect={item => handleAttachProduct(item)}
                  hasFilter={false}
                  hasTitle={false}
                >
                  <Button disabled={!staged.creator.id} >
                    {staged.product ? `${staged.product.productWork ? staged.product.productWork.title : (staged.product.title || 'No Title Found')} (${staged.product.format ? staged.product.format.description : 'No Format'} - ${staged.product.isbn || 'No ISBN'})` : 'Select Product...'}
                  </Button>
                </SelectMenu>
                {duplicateRoyaltyError && (
                  <Pane marginLeft={16} display="flex" alignItems="center">
                    <Paragraph color="danger">{duplicateRoyaltyError}</Paragraph>
                  </Pane>
                )}
              </Pane>

            </Pane>

          </Pane>
          {/* Conditionally render Region dropdown on a new line */}
          {enableSalesByRegion && (
            <Pane width="100%">
              {console.log("Rendering region dropdown with values:", availableRegions)}
              {console.log("Creator and product IDs:", {
                creatorId: staged.creator?.id,
                productId: staged.product?.id,
                isDisabled: !staged.creator.id || !staged.product.id
              })}
              <Paragraph color="#667b7f" size={300}>Region (Optional)
                <Popover
                  content={
                    <Pane padding={12}>
                      <Paragraph size={300}>
                        Adjust sales regions in settings. If no region is selected, royalty defaults to 'Rest of World'
                      </Paragraph>
                    </Pane>
                  }
                >
                  <HelpIcon size={12} marginLeft={8} color="muted" />
                </Popover>

              </Paragraph>

              {/* Simple test dropdown */}
              <Pane marginBottom={16}>
                <SelectMenu
                  title="Select Region"
                  options={availableRegions.map(region => ({ label: region.name, value: region.id }))}
                  selected={staged.region ? String(staged.region.id) : null}
                  onSelect={item => {
                    console.log("Direct select:", item);
                    const selectedRegion = availableRegions.find(r => r.id === item.value);
                    handleUpdateStaged({
                      key: "region",
                      val: selectedRegion
                    });
                  }}
                >
                  <Button>
                    {staged.region ? staged.region.name : 'Select Region...'}
                  </Button>
                </SelectMenu>
              </Pane>

            </Pane>
          )}
          <Pane marginTop={16}>
            <Paragraph size={400} fontWeight="bold" color="#5e5e5e" marginBottom={8}>REGULAR TIERS</Paragraph>

            <Table width={600}>
              <Table.Head height={40} className="table-heading">
                <Table.HeaderCell flex={2}>
                  Amount
                </Table.HeaderCell>
                <Table.HeaderCell flex={4}>
                  Term
                </Table.HeaderCell>
                <Table.HeaderCell flex={5} >
                  Tier Start/End
                </Table.HeaderCell>

                <Table.HeaderCell flex={1} >
                </Table.HeaderCell>

              </Table.Head>
              {sortItems(items, 1).map(item => {
                return (
                  <RoyaltyLineItem
                    key={`royaltyline${item.tmpid}`}
                    item={item}
                    handleUpdateItem={handleUpdateItem}
                    editing={true}
                    handleDelete={handleRemoveItem}
                  />
                );
              })}
            </Table>
            <Button
              iconBefore={AddIcon}
              onClick={e => handleAddItem({ id: 1, value: "units sold" }, "None")}
              size="small"
              marginTop={8}>
              ADD TIER
            </Button>            <Pane >
              <Paragraph size={400} fontWeight="bold" color="#5e5e5e" marginTop={16} marginBottom={8}>DISCOUNTED SALES TIERS</Paragraph>
              {sortItems(items, 2).length > 0 &&
                <Table width={600}>
                  <Table.Head height={40} className="table-heading">
                    <Table.HeaderCell flex={2}>
                      Amount
                    </Table.HeaderCell>
                    <Table.HeaderCell flex={4}>
                      Term
                    </Table.HeaderCell>
                    <Table.HeaderCell flex={5} >
                      Tier Start/End
                    </Table.HeaderCell>

                    <Table.HeaderCell flex={1} >
                    </Table.HeaderCell>

                  </Table.Head>
                  {sortItems(items, 2).map(item => {
                    if (item.first) {
                      return (
                        <Pane>
                          <Table.Row height={36} backgroundColor="var(--offwhite)" >
                            <Table.TextCell flex={1}>
                              <Paragraph size={300} marginLeft={16} fontWeight="bold" color="var(--darkgrey)">
                                If order discounted by at least
                                <TextInput
                                  className="item-form"
                                  width={50}
                                  marginY={0} id={item.tmpid} placeholder={item.tierDiscount} onChange={e => handleChangeDiscount(e)} marginLeft={10} />
                                %:</Paragraph>
                            </Table.TextCell>
                            <Table.TextCell flex={1}>
                              <Button size="small" iconBefore={ArrowDownIcon} onClick={e => handleAddItem({ id: 2, value: "discount" }, item.tierDiscount)}>
                                Add tier for sales discounted by {item.tierDiscount} %
                              </Button>
                            </Table.TextCell>
                          </Table.Row>
                          <RoyaltyLineItem
                            key={`royaltyline${item.tmpid}`}
                            item={item}
                            handleUpdateItem={handleUpdateItem}
                            editing={true}
                            handleDelete={handleRemoveItem}
                          />
                        </Pane>
                      )
                    }

                    else {
                      return (
                        <RoyaltyLineItem
                          key={`royaltyline${item.tmpid}`}
                          item={item}
                          handleUpdateItem={handleUpdateItem}
                          editing={true}
                          handleDelete={handleRemoveItem}
                        />
                      );
                    }
                  })}
                </Table>}
              <Button iconBefore={AddIcon} size="small"
                marginTop={8} onClick={e => handleAddItem({ id: 2, value: "discount" }, " ")}>ADD DISCOUNTED TIER SET</Button>
            </Pane>

          </Pane>

        </Card>

      </Pane>


    </Pane>
  )
}

export default NewRoyaltyForm;
